import { getActivePinia } from "pinia";
import { useUiStore } from "@/stores/ui";

/**
 * Post-deploy recovery when a dynamic import fails (stale chunk hash in the running app).
 *
 * A CDN may keep serving older hashed chunks for some time, which reduces how often this
 * fires — but it does not remove version skew: shell/HTML can update before or without
 * matching chunks, caches expire at different layers, purges happen, or the user keeps a
 * long-lived tab. This path is still the right safety net.
 *
 * Phase keys use `sessionStorage` (per-tab). Recovery in one tab does not affect another.
 */
const PHASE_KEY = "chunk_recovery_phase";
/** Prevents tight full-reload loops when the plain URL step still fails */
const PLAIN_RELOAD_GUARD_KEY = "chunk_plain_reload_at";

/** Minimum time between recovery attempts (coalesces duplicate router + preload errors). */
export const RECOVERY_DEDUPE_MS = 800;

/** Block back-to-back full reloads when the plain-URL step still fails immediately. */
export const FULL_RELOAD_GUARD_MS = 12_000;

/** Let the “Updating app…” snackbar paint before `assign` / `reload`. */
export const TOAST_GRACE_PERIOD_MS = 220;

/**
 * Defer `vite:preloadError` recovery so `router.onError` can run first with the real
 * destination `to` (lazy route chunks). Router recovery updates `lastRecoveryAt` synchronously;
 * this path then no-ops via dedupe. Non-route async chunks (e.g. modals) do not trigger
 * `router.onError`, so after the delay we recover using the current document URL.
 */
export const PRELOAD_RECOVERY_DEFER_MS = 400;

type RecoveryPhase = "" | "bust" | "plain";

let lastRecoveryAt = 0;

/**
 * True when the error message indicates a lazy chunk / asset preload failure
 * (typical after a deploy when hashed chunk filenames no longer exist).
 */
export function isChunkLoadErrorMessage(message: string): boolean {
  return (
    message.includes("Couldn't resolve component") ||
    message.includes("Loading chunk") ||
    message.includes("Loading CSS chunk") ||
    message.includes("ChunkLoadError") ||
    message.includes("Unable to preload CSS for") ||
    message.includes("Unable to preload") ||
    message.includes("dynamically imported module") ||
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed") ||
    message.includes("Load failed")
  );
}

function getPhase(): RecoveryPhase {
  const v = sessionStorage.getItem(PHASE_KEY);
  if (v === "bust" || v === "plain") return v;
  return "";
}

function setPhase(phase: RecoveryPhase): void {
  if (!phase) sessionStorage.removeItem(PHASE_KEY);
  else sessionStorage.setItem(PHASE_KEY, phase);
}

/** Call after a successful navigation so a later chunk error starts with cache-bust again. */
export function clearChunkRecoveryPhase(): void {
  setPhase("");
  sessionStorage.removeItem(PLAIN_RELOAD_GUARD_KEY);
}

function withCacheBust(fullPath: string): string {
  const u = new URL(fullPath, window.location.origin);
  u.searchParams.set("_chunk_cb", String(Date.now()));
  return u.pathname + u.search + u.hash;
}

/** Remove `_chunk_cb` so step 2 is a plain URL, not a repeat of the cache-busted URL. */
function stripChunkCacheBust(fullPath: string): string {
  const u = new URL(fullPath, window.location.origin);
  u.searchParams.delete("_chunk_cb");
  return u.pathname + u.search + u.hash;
}

function showUpdatingToastThen(run: () => void): void {
  const pinia = getActivePinia();
  if (pinia) {
    try {
      useUiStore(pinia).setChunkRecoveryToastVisible(true);
    } catch {
      /* Pinia may be unavailable in edge cases */
    }
  }
  window.setTimeout(run, TOAST_GRACE_PERIOD_MS);
}

/**
 * Recover from a failed dynamic import: cache-bust URL → plain URL → guarded full reload.
 * Uses sessionStorage so steps survive a document navigation.
 */
export function recoverFromChunkLoadFailure(fullPath: string): void {
  if (typeof window === "undefined") return;

  const now = Date.now();
  if (now - lastRecoveryAt < RECOVERY_DEDUPE_MS) return;
  lastRecoveryAt = now;

  const phase = getPhase();

  if (phase === "") {
    setPhase("bust");
    showUpdatingToastThen(() => {
      window.location.assign(withCacheBust(fullPath));
    });
    return;
  }

  if (phase === "bust") {
    setPhase("plain");
    showUpdatingToastThen(() => {
      window.location.assign(stripChunkCacheBust(fullPath));
    });
    return;
  }

  // phase === "plain" — last resort: full reload (separate guard from earlier steps)
  const lastPlain = sessionStorage.getItem(PLAIN_RELOAD_GUARD_KEY);
  const lastPlainMs = lastPlain ? Number.parseInt(lastPlain, 10) : 0;
  if (
    lastPlainMs &&
    !Number.isNaN(lastPlainMs) &&
    now - lastPlainMs < FULL_RELOAD_GUARD_MS
  ) {
    console.error(
      "[ChunkRecovery] Repeated chunk load failures; not reloading again (guard)",
      { fullPath },
    );
    return;
  }

  sessionStorage.setItem(PLAIN_RELOAD_GUARD_KEY, String(now));
  setPhase("");
  showUpdatingToastThen(() => {
    window.location.reload();
  });
}

/**
 * Recover from Vite preload/dynamic import failures outside of Vue Router (e.g. `defineAsyncComponent`
 * modals). Deferred so lazy-route failures are handled by `router.onError` + `to.fullPath` first.
 */
export function scheduleRecoverFromStaleChunkPreload(): void {
  if (typeof window === "undefined") return;
  window.setTimeout(() => {
    const docPath =
      window.location.pathname +
      window.location.search +
      window.location.hash;
    recoverFromChunkLoadFailure(docPath);
  }, PRELOAD_RECOVERY_DEFER_MS);
}
